import { msg } from "@lingui/macro";
import { useLingui } from "@lingui/macro";
import type { MessageDescriptor } from "@lingui/core";
import type { CorrectionErrorClass } from "@app/types/correction";

/**
 * The French wording of the eight error classes, and the hook that reads it back.
 *
 * Separate from the picker component on purpose: the labels are also needed by read-only
 * surfaces, and a shared constant living in a component file breaks fast refresh for
 * everything that imports it.
 *
 * The class VALUES are identifiers that travel to the database and must never be
 * translated; only these labels are. Keeping the two apart is what lets the wording change
 * after a conversation with the practitioners without rewriting a single stored row.
 */

export interface ErrorClassOption {
  value: CorrectionErrorClass;
  label: MessageDescriptor;
  /** Shown under the label — a taxonomy a doctor has to guess at yields confident wrong
   * labels rather than hesitation, which is the failure mode that costs the most. */
  hint: MessageDescriptor;
}

/**
 * The two groups the picker renders under headings.
 *
 * They are not cosmetic: "the corrected value is somewhere in this record" is a fact the
 * backend can often check, and it eliminates three of the eight without choosing among the
 * rest. Rendering the groups is what lets a narrowed suggestion be shown as a narrowing
 * rather than as nothing.
 */
export const IN_RECORD_ORDER: CorrectionErrorClass[] = [
  "MISSED_IN_SOURCE",
  "MISREAD_SOURCE",
  "WRONG_FIELD",
  "OVER_EXTRACTED",
  "VOCABULARY",
];

export const NOT_IN_RECORD_ORDER: CorrectionErrorClass[] = [
  "ABSENT_FROM_SOURCE",
  "MISATTRIBUTED",
  "CLINICAL_JUDGEMENT",
];

const OPTIONS_BY_VALUE: Record<CorrectionErrorClass, ErrorClassOption> = {
  MISSED_IN_SOURCE: {
    value: "MISSED_IN_SOURCE",
    label: msg`C'est écrit dans un document`,
    hint: msg`L'information est présente, le modèle ne l'a pas relevée.`,
  },
  MISREAD_SOURCE: {
    value: "MISREAD_SOURCE",
    label: msg`Le passage a été mal interprété`,
    hint: msg`Le document dit juste, mais le modèle en a tiré une autre valeur.`,
  },
  WRONG_FIELD: {
    value: "WRONG_FIELD",
    label: msg`Ça aurait dû aller dans un autre champ`,
    hint: msg`La valeur est juste, mais elle appartient à un autre champ du dossier.`,
  },
  OVER_EXTRACTED: {
    value: "OVER_EXTRACTED",
    label: msg`Ce champ aurait dû rester vide`,
    hint: msg`Le texte dit quelque chose, mais cela ne relève pas de ce champ : négation, acte seulement prévu, antécédent d'un proche...`,
  },
  VOCABULARY: {
    value: "VOCABULARY",
    label: msg`Question de vocabulaire`,
    hint: msg`La valeur voulait dire la bonne chose sous un mauvais libellé (synonyme, variante).`,
  },
  ABSENT_FROM_SOURCE: {
    value: "ABSENT_FROM_SOURCE",
    label: msg`Ça ne figure nulle part`,
    hint: msg`Aucun document ne mentionne cette valeur : elle a été inventée.`,
  },
  MISATTRIBUTED: {
    value: "MISATTRIBUTED",
    label: msg`Information mal attribuée`,
    hint: msg`L'information existe mais concerne un autre patient, un autre épisode ou une autre date.`,
  },
  CLINICAL_JUDGEMENT: {
    value: "CLINICAL_JUDGEMENT",
    // Also covers a wrong document (dictation error, typo): same repair, so the same class.
    label: msg`Le document se trompe ou n'en dit rien`,
    hint: msg`La bonne valeur ne pouvait pas être lue : aucun document ne la donne, ou celui qui la donne est erroné (erreur de dictée, coquille). Elle vient de votre connaissance du dossier.`,
  },
};

export function errorClassOption(
  value: CorrectionErrorClass,
): ErrorClassOption {
  return OPTIONS_BY_VALUE[value];
}

/** French label for a class, for read-only surfaces. */
export function useErrorClassLabel() {
  const { t } = useLingui();
  return (value: CorrectionErrorClass | null): string => {
    if (!value) return t`Non classée`;
    const option = OPTIONS_BY_VALUE[value];
    return option ? t(option.label) : value;
  };
}
